import { useStore } from 'dg-web-shared/lib/Flux';
import { logAction } from '../../utils/ActionLog';

import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isRunningInNativeWrapper } from '../../Native';
import * as LocalStorage from 'dg-web-shared/lib/LocalStorage';
import { AuthStatusType, useAuthState } from './AuthState';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { languageFromString } from 'dg-web-shared/lib/Localized.ts';

type Web = {
    platform: 'Web';
};

type Native = {
    platform: 'Native';
};

type iOS = Native & {
    os: 'iOS';
    iosVersion: string;
    iosBuild: string;
};

type Android = Native & {
    os: 'Android';
    androidRelease: string;
    androidAppVersion: string;
};

type Device = Web | iOS | Android;

function useDevice(): Device | null {
    const [searchParams] = useSearchParams();
    return useMemo<Device | null>(() => {
        if (!isRunningInNativeWrapper()) {
            return { platform: 'Web' };
        }

        const iosVersion = searchParams.get('ios_version');
        const iosBuild = searchParams.get('ios_build');
        if (iosVersion && iosBuild) {
            return {
                platform: 'Native',
                os: 'iOS',
                iosVersion,
                iosBuild,
            };
        }

        const androidRelease = searchParams.get('android_release');
        const androidAppVersion = searchParams.get('android_app_version');
        if (androidRelease && androidAppVersion) {
            return {
                platform: 'Native',
                os: 'Android',
                androidRelease,
                androidAppVersion,
            };
        }

        return null;
    }, [searchParams]);
}

function useLanguageFromQueryParams() {
    const [searchParams] = useSearchParams();
    const { setLanguage } = useLanguage();
    useEffect(() => {
        const lang = searchParams.get('lang');
        const savedLanguage = LocalStorage.Settings.getUiLanguageAsStringId();
        if (savedLanguage.isNull && lang != null) {
            setLanguage(languageFromString(lang));
        }
    }, [searchParams, setLanguage]);
}

export function AppStartupHooks({ children }: { children: React.ReactNode }) {
    const { store } = useStore(() => null);

    const authState = useAuthState();

    const device = useDevice();
    useLanguageFromQueryParams();

    useEffect(() => {
        if (authState.authStatus === AuthStatusType.OK) {
            logAction(store, 'enter-app-as-customer', device);
        }
    }, [authState.authStatus, device, store]);

    return <>{children}</>;
}
